import { useCallback, useEffect, useState } from 'react';
import { ApiError, api } from '@/api/client';
import type { Checkout as CheckoutParams, Subscription } from '@/api/types';
import { Button, Card } from '@/components/ui';
import { useT } from '@/i18n';
import { useSession } from '@/hooks/session';
import { formatMoney } from '@/lib/format';
import { loadRazorpay } from '@/lib/razorpay';

type Outcome = 'success' | 'pending' | 'failed' | 'cancelled';

export interface PlusSubscribeProps {
  /** Fires once a subscription is created and verified as active (mock approve or the
   * Razorpay handler) — lets the caller react, e.g. navigate away. */
  onActivated?: () => void;
  /**
   * When supplied, rendered as the action for an active subscription with quota remaining,
   * in place of a plain status line — e.g. Checkout's "spend this session's unlock", which
   * needs a specific assessmentId this component doesn't know about. A rejection is surfaced
   * as the card's own error text.
   */
  onUseUnlock?: () => Promise<void>;
  /** Blocks starting a new subscription, e.g. until Checkout has the parent's state for the GST invoice. */
  disabled?: boolean;
}

/**
 * Nurture Plus subscribe card — fetches the current subscription on mount and offers
 * whichever action fits its status: subscribe (mock simulator or Razorpay), spend a monthly
 * unlock (only when `onUseUnlock` is supplied), or an "exhausted" note. Reused by the
 * session checkout's Plus card and the session-less `/plus` page. Cancelling the plan is a
 * Settings-only action and lives outside this component.
 */
export const PlusSubscribe = ({ onActivated, onUseUnlock, disabled = false }: PlusSubscribeProps) => {
  const t = useT();
  const { config } = useSession();
  const plusPlan = config?.plans.plus;

  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [busy, setBusy] = useState(false);
  const [payBusy, setPayBusy] = useState(false);
  const [checkout, setCheckout] = useState<CheckoutParams | null>(null);
  const [subscriptionId, setSubscriptionId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const refreshSubscription = useCallback(async () => {
    const { subscription: sub } = await api.subscriptions.current();
    setSubscription(sub ?? null);
  }, []);

  useEffect(() => {
    if (!plusPlan) return undefined;
    let cancelled = false;
    void api.subscriptions
      .current()
      .then(({ subscription: sub }) => {
        if (!cancelled) setSubscription(sub ?? null);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [plusPlan]);

  if (!plusPlan) return null;

  const active = subscription?.status === 'active';
  const hasQuota = active && subscription!.quota.remaining > 0;
  const exhausted = active && !hasQuota;

  const start = async () => {
    setBusy(true);
    setError(null);
    try {
      const { subscription: sub, checkout: gw } = await api.subscriptions.create();
      setSubscriptionId(sub.subscriptionId);
      setCheckout(gw);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('common.error'));
    } finally {
      setBusy(false);
    }
  };

  const verify = async (outcome: Outcome) => {
    if (!subscriptionId) return;
    setPayBusy(true);
    setError(null);
    try {
      await api.subscriptions.verify(subscriptionId, { outcome });
      setCheckout(null);
      setSubscriptionId(null);
      if (outcome === 'success') {
        await refreshSubscription();
        onActivated?.();
      } else if (outcome === 'failed') {
        setError(t('checkout.failedBody'));
      }
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('common.error'));
    } finally {
      setPayBusy(false);
    }
  };

  const payRazorpay = async () => {
    if (!subscriptionId || !checkout) return;
    setPayBusy(true);
    setError(null);
    try {
      const ready = await loadRazorpay();
      if (!ready || !window.Razorpay) {
        setError(t('common.error'));
        return;
      }
      const rzp = new window.Razorpay({
        key: checkout.key,
        subscription_id: checkout.subscriptionId,
        name: checkout.name,
        description: checkout.description,
        prefill: checkout.prefill,
        handler: async (response: Record<string, string>) => {
          await api.subscriptions.verify(subscriptionId, {
            paymentId: response.razorpay_payment_id,
            signature: response.razorpay_signature,
          });
          setCheckout(null);
          setSubscriptionId(null);
          await refreshSubscription();
          onActivated?.();
        },
        modal: {
          ondismiss: async () => {
            await api.subscriptions.verify(subscriptionId, { outcome: 'cancelled' });
            setCheckout(null);
            setSubscriptionId(null);
          },
        },
      });
      rzp.open();
    } finally {
      setPayBusy(false);
    }
  };

  const useUnlock = async () => {
    if (!onUseUnlock) return;
    setBusy(true);
    setError(null);
    try {
      await onUseUnlock();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('common.error'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card>
      <h2 className="text-h2 text-ink">{t('plan.plus.title')}</h2>
      <p className="mt-1 text-caption text-ink-muted">
        {t('plan.plus.body', { n: plusPlan.unlocksPerMonth, price: formatMoney(plusPlan.amount, plusPlan.currency) })}
      </p>

      {error && (
        <p role="alert" className="mt-2 text-caption text-alert-ink">
          {error}
        </p>
      )}

      {hasQuota ? (
        <>
          <p className="mt-3 text-label text-ink">
            {t('plan.plus.remaining', { n: subscription!.quota.remaining, total: subscription!.quota.perMonth })}
          </p>
          {onUseUnlock && (
            <Button full className="mt-3" loading={busy} onClick={() => void useUnlock()}>
              {t('plan.plus.use')}
            </Button>
          )}
        </>
      ) : exhausted ? (
        <p className="mt-3 text-caption text-ink-muted">{t('plan.plus.exhausted')}</p>
      ) : (
        !checkout && (
          <Button full className="mt-3" loading={busy} disabled={disabled} onClick={() => void start()}>
            {t('plan.plus.cta')}
          </Button>
        )
      )}

      {checkout?.gateway === 'mock' && (
        <div className="mt-4 space-y-2">
          <p className="text-label text-teal-700">{t('checkout.mockTitle')}</p>
          <Button full loading={payBusy} onClick={() => void verify('success')}>
            {t('checkout.mockApprove')}
          </Button>
          <Button full variant="secondary" loading={payBusy} onClick={() => void verify('pending')}>
            {t('checkout.mockPending')}
          </Button>
          <Button full variant="secondary" loading={payBusy} onClick={() => void verify('failed')}>
            {t('checkout.mockFail')}
          </Button>
          <Button full variant="tertiary" onClick={() => void verify('cancelled')}>
            {t('checkout.mockCancel')}
          </Button>
        </div>
      )}

      {checkout?.gateway === 'razorpay' && (
        <Button full className="mt-4" loading={payBusy} onClick={() => void payRazorpay()}>
          {t('checkout.cta')}
        </Button>
      )}
    </Card>
  );
};
